import {
  ActionIcon,
  Avatar,
  Box,
  Button,
  Drawer,
  Group,
  LoadingOverlay,
  Select,
  Text,
  TextInput,
  Tooltip,
  UnstyledButton,
  rem,
} from "@mantine/core";
import {
  CheckIcon,
  ChecksIcon,
  FunnelXIcon,
  ImageIcon,
  MagnifyingGlassIcon,
  PlusIcon,
  XIcon,
} from "@phosphor-icons/react";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import * as ErpInvoiceApi from "../../apis/erp-invoice";
import * as PriceApi from "../../apis/price";
import { currencyFormat } from "../../utils";
import { ColumnType, DataTable, ITableRef } from "../data-table";
import { AppModalHeader } from "../ui/app-modal";
import modalChrome from "../ui/app-modal.module.css";
import classes from "./VariantSelectorDrawer.module.css";

interface Props {
  opened: boolean;
  onClose: () => void;
  businessId: string;
  buyerBusinessId?: string | null;
  buyerName?: string | null;
  selectedIds: string[];
  selectedSeed?: any[];
  onAdd: (variant: any) => void | Promise<void>;
  onAddAll?: (variants: any[]) => void | Promise<void>;
  subtitle?: string;
}

type IFilter = {
  query: string;
  categoryId: string | null;
  brandId: string | null;
};

const EMPTY_FILTERS: IFilter = {
  query: "",
  categoryId: null,
  brandId: null,
};

type PriceRow = { finalPrice: number; b2bPrice: number | null; b2cPrice: number | null };
type ListTab = "all" | "added";
type PriceTone = "b2b" | "b2c" | "final";

function PriceCell({ value, tone }: { value: number | null | undefined; tone: PriceTone }) {
  const toneClass =
    tone === "b2b" ? classes.priceB2b : tone === "b2c" ? classes.priceB2c : classes.priceFinal;

  if (value == null || value === 0) {
    return (
      <Text className={`${classes.num} ${toneClass}`}>
        -
      </Text>
    );
  }
  return (
    <Text className={`${classes.num} ${toneClass}`}>
      {currencyFormat(value, { withSymbol: false })}
    </Text>
  );
}

export function VariantSelectorDrawer({
  opened,
  onClose,
  businessId,
  buyerBusinessId,
  buyerName,
  selectedIds,
  selectedSeed,
  onAdd,
  onAddAll,
  subtitle,
}: Props) {
  const { t } = useTranslation("invoice");
  const [filters, setFilters] = useState<IFilter>({ ...EMPTY_FILTERS });
  const [listTab, setListTab] = useState<ListTab>("all");
  const refTable = useRef<ITableRef>(null);
  const [addingAll, setAddingAll] = useState(false);
  const [tableRows, setTableRows] = useState<any[]>([]);
  const [categories, setCategories] = useState<any[]>([]);
  const [brands, setBrands] = useState<any[]>([]);
  const [totalCount, setTotalCount] = useState(0);
  const selectedCacheRef = useRef<Record<string, any>>({});
  const priceCacheRef = useRef<Record<string, number>>({});

  const apiFilters = useMemo(
    () => ({
      query: filters.query || null,
      categoryId: filters.categoryId,
      brandId: filters.brandId,
      warehouseId: null,
    }),
    [filters],
  );

  const hasFilters = filters.categoryId !== null || filters.brandId !== null || !!filters.query;

  const visibleVariantIds = useMemo(() => tableRows.map((r: any) => r.id).filter(Boolean), [tableRows]);

  const { data: priceBreakdowns } = useSWR(
    opened && buyerBusinessId && visibleVariantIds.length > 0
      ? `swr.invoice.price.batch.${buyerBusinessId}.${visibleVariantIds.join(",")}`
      : null,
    () =>
      PriceApi.calculateBatch({
        buyerBusinessId: buyerBusinessId!,
        variants: visibleVariantIds.map((id: string) => ({ variantId: id, quantity: 1 })),
      }),
    { revalidateOnFocus: false, keepPreviousData: true },
  );

  const priceByVariant = useMemo(() => {
    const map: Record<string, PriceRow> = {};
    for (const pb of (priceBreakdowns || []) as any[]) {
      map[pb.variantId] = {
        finalPrice: pb.finalPrice ?? 0,
        b2bPrice: pb.basePrice?.b2bPrice ?? pb.b2bPrice ?? null,
        b2cPrice: pb.basePrice?.b2cPrice ?? pb.b2cPrice ?? null,
      };
    }
    return map;
  }, [priceBreakdowns]);

  useEffect(() => {
    for (const r of tableRows) {
      const p = priceByVariant[r.id]?.finalPrice ?? r.price ?? r.standardPrice ?? r.b2bPrice;
      if (p != null && Number(p) > 0) priceCacheRef.current[r.id] = Number(p);
    }
  }, [tableRows, priceByVariant]);

  useEffect(() => {
    if (!opened) {
      setListTab("all");
      setFilters({ ...EMPTY_FILTERS });
      return;
    }
    for (const row of selectedSeed || []) {
      if (row?.id) {
        selectedCacheRef.current[row.id] = row;
        if (row.price != null && Number(row.price) > 0) {
          priceCacheRef.current[row.id] = Number(row.price);
        }
      }
    }
  }, [opened, selectedSeed]);

  const enrichVariant = (record: any) => {
    const priceRow = priceByVariant[record.id];
    if (priceRow?.finalPrice && !record.price) {
      return { ...record, price: priceRow.finalPrice };
    }
    return record;
  };

  const rememberAndAdd = async (record: any) => {
    const enriched = enrichVariant(record);
    const p = enriched.price ?? enriched.standardPrice ?? priceByVariant[record.id]?.finalPrice;
    if (p != null && Number(p) > 0) priceCacheRef.current[record.id] = Number(p);
    selectedCacheRef.current[record.id] = enriched;
    await onAdd(enriched);
  };

  const categoryOptions = useMemo(
    () =>
      (categories || [])
        .filter((c: any) => c?.id && c?.name)
        .map((c: any) => ({ value: c.id, label: c.name })),
    [categories],
  );

  const brandOptions = useMemo(
    () =>
      (brands || [])
        .filter((b: any) => b?.id && b?.name)
        .map((b: any) => ({ value: b.id, label: b.name })),
    [brands],
  );

  const selectedTotal = useMemo(() => {
    return selectedIds.reduce((sum, id) => {
      const cached = priceCacheRef.current[id];
      const live = priceByVariant[id]?.finalPrice;
      const row = tableRows.find((r) => r.id === id) || selectedCacheRef.current[id];
      const p = live ?? cached ?? row?.price ?? row?.standardPrice ?? 0;
      return sum + Number(p || 0);
    }, 0);
  }, [selectedIds, priceByVariant, tableRows]);

  const addedRows = useMemo(() => {
    return selectedIds
      .map((id) => selectedCacheRef.current[id] || tableRows.find((r) => r.id === id))
      .filter(Boolean);
  }, [selectedIds, tableRows]);

  const handleAddPage = async () => {
    try {
      setAddingAll(true);
      const selectedSet = new Set(selectedIds);
      const unselected = tableRows.filter((r: any) => !selectedSet.has(r.id)).map(enrichVariant);
      for (const v of unselected) {
        const p = v.price ?? v.standardPrice ?? v.b2bPrice ?? priceByVariant[v.id]?.finalPrice;
        if (p != null && Number(p) > 0) priceCacheRef.current[v.id] = Number(p);
        selectedCacheRef.current[v.id] = v;
      }
      if (unselected.length === 0) return;
      if (onAddAll) {
        await onAddAll(unselected);
      } else {
        for (const v of unselected) await onAdd(v);
      }
    } finally {
      setAddingAll(false);
    }
  };

  const columns: ColumnType<any>[] = [
    {
      title: <span className={classes.srOnly}>{t("drawer.add")}</span>,
      sticky: "actions",
      align: "left",
      width: 44,
      render: (record) => {
        const isSelected = selectedIds.includes(record.id);
        return (
          <Tooltip label={isSelected ? t("drawer.added") : t("drawer.add")} withArrow position="right">
            <ActionIcon
              className={classes.addBtn}
              data-selected={isSelected || undefined}
              variant="subtle"
              size={28}
              radius="xl"
              disabled={isSelected}
              onClick={() => void rememberAndAdd(record)}
            >
              {isSelected ? <CheckIcon size={14} weight="bold" /> : <PlusIcon size={14} />}
            </ActionIcon>
          </Tooltip>
        );
      },
    },
    {
      title: t("drawer.product"),
      sorter: listTab === "all",
      dataIndex: "name",
      className: classes.colProduct,
      render: (record) => {
        const variantLabel = record?.optionValueNames?.length > 0 ? record.optionValueNames.join(", ") : null;
        const fullName = [record?.nameMon ?? record?.name ?? "-", variantLabel ? `- ${variantLabel}` : null]
          .filter(Boolean)
          .join(" ");
        return (
          <Group gap="sm" wrap="nowrap" className={classes.productCell} miw={0}>
            <Avatar
              src={record.image ?? undefined}
              className={classes.avatar}
              radius="sm"
              size={40}
            >
              <ImageIcon size={14} />
            </Avatar>
            <Box miw={0} style={{ flex: 1, overflow: "hidden" }}>
              <Tooltip label={fullName} withArrow disabled={!fullName || fullName.length < 40}>
                <Text className={classes.productName} title={fullName}>
                  {fullName}
                </Text>
              </Tooltip>
              <Text className={classes.sku} mt={2}>
                {record.skuCode ?? "-"}
              </Text>
            </Box>
          </Group>
        );
      },
    },
    {
      title: t("drawer.category"),
      width: 140,
      className: classes.cellNowrap,
      render: (record) => (
        <Text size="sm" c="dimmed" truncate className={classes.cellNowrap}>
          {record.category?.name || record.subCategory?.name || "-"}
        </Text>
      ),
    },
    {
      title: t("drawer.unit"),
      width: 100,
      className: classes.cellNowrap,
      render: (record) => (
        <Text size="sm" c="dimmed" truncate className={classes.cellNowrap}>
          {record.unit?.name || record.unit || record.baseUnit?.name || record.baseUnit || "-"}
        </Text>
      ),
    },
    {
      title: "B2B",
      width: 110,
      align: "right",
      className: classes.cellNowrap,
      render: (record: any) => {
        const row = priceByVariant[record.id];
        return <PriceCell value={row?.b2bPrice ?? record.b2bPrice} tone="b2b" />;
      },
    },
    {
      title: "B2C",
      width: 110,
      align: "right",
      className: classes.cellNowrap,
      render: (record: any) => {
        const row = priceByVariant[record.id];
        return <PriceCell value={row?.b2cPrice ?? record.b2cPrice} tone="b2c" />;
      },
    },
    {
      title: t("drawer.yourPrice"),
      width: 120,
      align: "right",
      className: classes.cellNowrap,
      render: (record: any) => {
        const row = priceByVariant[record.id];
        const value = row?.finalPrice ?? record.price ?? record.standardPrice ?? null;
        return <PriceCell value={value} tone="final" />;
      },
    },
  ];

  // Name is 13/600 gray-9; suffix ("-д…") must abut the name — no JSX whitespace node.
  const hintNode = subtitle ? (
    <Text className={classes.hint}>{subtitle}</Text>
  ) : buyerName ? (
    <Text className={classes.hint}>
      {t("drawer.subtitleForBuyerPrefix")}
      <Text span className={classes.hintName}>{buyerName}</Text>{t("drawer.subtitleForBuyerSuffix")}
    </Text>
  ) : (
    <Text className={classes.hint}>{t("drawer.subtitle")}</Text>
  );

  const hasSelection = selectedIds.length > 0;

  return (
    <Drawer
      size="lg"
      opened={opened}
      onClose={addingAll ? () => {} : onClose}
      closeOnClickOutside={!addingAll}
      closeOnEscape={!addingAll}
      title={false}
      withCloseButton={false}
      classNames={{ body: modalChrome.drawerBodyFlush }}
      styles={{
        content: { display: "flex", flexDirection: "column", overflow: "hidden" },
        body: {
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          overflow: "hidden",
          padding: 0,
        },
      }}
    >
      <Box className={classes.root}>
        <LoadingOverlay visible={addingAll} zIndex={200} overlayProps={{ blur: 2 }} />

        <AppModalHeader
          bordered
          title={t("drawer.title")}
          subtitle={hintNode}
          onClose={onClose}
          closeDisabled={addingAll}
        />

        <Box className={classes.filters}>
          <TextInput
            className={classes.search}
            placeholder={t("drawer.searchPlaceholder")}
            leftSection={<MagnifyingGlassIcon size={16} />}
            size="sm"
            value={filters.query}
            onChange={(e) => setFilters({ ...filters, query: e.currentTarget.value })}
            rightSection={
              filters.query ? (
                <ActionIcon
                  size="sm"
                  variant="subtle"
                  c="dimmed"
                  onClick={() => setFilters({ ...filters, query: "" })}
                >
                  <XIcon size={14} />
                </ActionIcon>
              ) : null
            }
          />
          <Select
            className={classes.filterSelect}
            placeholder={`${t("drawer.category")}: ${t("drawer.all")}`}
            data={categoryOptions}
            value={filters.categoryId}
            onChange={(categoryId) => setFilters({ ...filters, categoryId })}
            searchable
            clearable
            size="sm"
          />
          <Select
            className={classes.filterSelect}
            placeholder={`${t("drawer.brand")}: ${t("drawer.all")}`}
            data={brandOptions}
            value={filters.brandId}
            onChange={(brandId) => setFilters({ ...filters, brandId })}
            searchable
            clearable
            size="sm"
          />
          <Button
            className={classes.clearBtn}
            variant="default"
            size="sm"
            leftSection={<FunnelXIcon size={14} />}
            disabled={!hasFilters}
            onClick={() => setFilters({ ...EMPTY_FILTERS })}
          >
            {t("drawer.clear")}
          </Button>
        </Box>

        <Box className={classes.toolbar}>
          <Group gap={4}>
            <UnstyledButton
              className={classes.tab}
              data-active={listTab === "all" || undefined}
              onClick={() => setListTab("all")}
            >
              {t("drawer.tabAll", { count: totalCount || "…" })}
            </UnstyledButton>
            <UnstyledButton
              className={classes.tab}
              data-active={listTab === "added" || undefined}
              onClick={() => setListTab("added")}
            >
              {t("drawer.tabAdded", { count: selectedIds.length })}
            </UnstyledButton>
          </Group>
          {listTab === "all" ? (
            <Button
              variant="default"
              size="compact-sm"
              leftSection={<ChecksIcon size={14} />}
              onClick={() => void handleAddPage()}
              loading={addingAll}
              disabled={tableRows.length === 0}
            >
              {t("drawer.addPage")}
            </Button>
          ) : null}
        </Box>

        <Box className={classes.body}>
          {opened && businessId && listTab === "all" ? (
            <DataTable
              ref={refTable}
              name="invoice.variant-selector.list"
              columns={columns}
              minWidth={780}
              className={classes.tableCard}
              paginationClassName={classes.paginationBar}
              withTableBorder={false}
              showPaginationRange
              filters={apiFilters}
              loadData={(data: any) =>
                ErpInvoiceApi.variant_list({
                  filter: data?.filter ?? apiFilters,
                  offset: data?.offset ?? { page: 1, limit: 10 },
                  businessId,
                })
              }
              getRowProps={(row) =>
                selectedIds.includes(row.id)
                  ? { className: classes.rowSelected, "data-selected": true }
                  : {}
              }
              onResult={(res: any) => {
                const rows = res?.rows || [];
                setTableRows((prev) => {
                  if (prev.length === rows.length && prev.every((r, i) => r?.id === rows[i]?.id)) return prev;
                  return rows;
                });
                if (typeof res?.count === "number") setTotalCount(res.count);
                const cats = res?.categories || [];
                setCategories((prev) => {
                  if (prev.length === cats.length && prev.every((c, i) => c?.id === cats[i]?.id)) return prev;
                  return cats;
                });
                const brs = res?.brands || [];
                setBrands((prev) => {
                  if (prev.length === brs.length && prev.every((b, i) => b?.id === brs[i]?.id)) return prev;
                  return brs;
                });
              }}
            />
          ) : null}

          {opened && listTab === "added" ? (
            <DataTable
              name="invoice.variant-selector.added"
              columns={columns}
              minWidth={780}
              className={classes.tableCard}
              withTableBorder={false}
              dataSource={addedRows}
              pagination={false}
              getRowProps={() => ({ className: classes.rowSelected, "data-selected": true })}
            />
          ) : null}
        </Box>

        <Box className={classes.footer}>
          <Group gap="sm" wrap="nowrap">
            <Box className={classes.countBadge} data-empty={hasSelection ? undefined : true}>
              {selectedIds.length}
            </Box>
            {hasSelection ? (
              <Text className={classes.footerMeta}>
                {selectedIds.length} {t("drawer.footerAddedLabel")}
                {" · "}
                {t("drawer.footerLineTotal")}{" "}
                <Text span className={`${classes.num} ${classes.footerAmount}`}>
                  {currencyFormat(selectedTotal, { withSymbol: true })}
                </Text>
              </Text>
            ) : (
              <Text className={classes.footerMeta}>{t("drawer.footerEmpty")}</Text>
            )}
          </Group>
          <Button
            className={classes.footerDone}
            color="brand"
            onClick={onClose}
            disabled={addingAll}
          >
            {t("drawer.done")}
          </Button>
        </Box>
      </Box>
    </Drawer>
  );
}
